import * as THREE from "three";
import type { LoadedHuman } from "./assetLoader";
import type {
  PhysicsWorld,
  RapierCollider,
  RapierImpulseJoint,
  RapierRigidBody,
} from "./physicsWorld";

const MIN_BONE_LEN = 0.04;
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _childPos = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _yAxis = new THREE.Vector3(0, 1, 0);
const _mat = new THREE.Matrix4();
const _parentInv = new THREE.Matrix4();
const _localPos = new THREE.Vector3();
const _localQuat = new THREE.Quaternion();
const _localScale = new THREE.Vector3();
const _collQuat = new THREE.Quaternion();

export type BoneBodyMode = "kinematic" | "dynamic" | "fixed";

export interface BoneBodyEntry {
  bone: THREE.Bone;
  humanId: string;
  body: RapierRigidBody;
  collider: RapierCollider;
  joint: RapierImpulseJoint | null;
  /** Parent bone entry (joint target), if any. */
  parentEntry: BoneBodyEntry | null;
  halfHeight: number;
  radius: number;
}

/** Floor membership bit (matches PhysicsWorld.setFloor). */
const FLOOR_MEMBER = 0x0002;
/** Max humans with colliders (tier high cap). */
const MAX_COLLIDER_HUMANS = 6;

/** Human i membership: bit (i+2) → 0x0004, 0x0008, … */
function humanMembership(humanIndex: number): number {
  return 1 << (humanIndex + 2);
}

/** Collide with floor + every other human set; never self (avoids joint jitter). */
function humanFilter(humanIndex: number, humanCount: number): number {
  let filter = FLOOR_MEMBER;
  for (let i = 0; i < humanCount; i++) {
    if (i !== humanIndex) filter |= humanMembership(i);
  }
  return filter & 0xffff;
}

function packGroups(membership: number, filter: number): number {
  return ((membership & 0xffff) << 16) | (filter & 0xffff);
}

/**
 * Auto capsule colliders + spherical joints for skinned human skeletons.
 * Rigid body transform == bone world transform (simplifies ragdoll write-back).
 * Capsule is offset/rotated in collider local space along bone→child.
 * H3: shared world; body sets collide with floor and *other* humans (not self).
 */
export class BoneColliders {
  private physics: PhysicsWorld | null = null;
  private entries: BoneBodyEntry[] = [];
  /** Root→leaf order for writing transforms back. */
  private ordered: BoneBodyEntry[] = [];
  private mode: BoneBodyMode = "kinematic";
  /** Human ids allowed to become dynamic under ragdoll (tier budget). */
  private dynamicHumanIds = new Set<string>();
  private disposed = false;

  getEntries(): readonly BoneBodyEntry[] {
    return this.entries;
  }

  /** Ids of humans that receive dynamic ragdoll under global ragdoll mode. */
  getDynamicHumanIds(): readonly string[] {
    return [...this.dynamicHumanIds];
  }

  isHumanDynamic(humanId: string): boolean {
    return this.dynamicHumanIds.has(humanId);
  }

  /**
   * Rebuild capsules for humans. Honors `def.boneColliders === "none"`.
   * All eligible humans get colliders (for inter-body contact).
   * Only the first `maxDynamic` humans may become dynamic ragdolls (tier gate).
   */
  rebuild(
    physics: PhysicsWorld,
    humans: LoadedHuman[],
    maxDynamic: number = 6,
  ): void {
    this.clear();
    this.physics = physics;
    this.dynamicHumanIds.clear();
    if (this.disposed) return;

    const eligible = humans.filter((h) => h.def.boneColliders !== "none");
    const toBuild = eligible.slice(0, MAX_COLLIDER_HUMANS);
    let dynamicSlots = Math.max(0, maxDynamic);

    for (let i = 0; i < toBuild.length; i++) {
      const human = toBuild[i];
      if (dynamicSlots > 0) {
        this.dynamicHumanIds.add(human.def.id);
        dynamicSlots--;
      }
      this.buildHuman(physics, human, i, toBuild.length);
    }
    this.ordered = topologicalOrder(this.entries);
    this.applyMode(this.mode);
  }

  setMode(mode: BoneBodyMode): void {
    if (this.mode === mode) {
      if (mode === "dynamic") this.syncFromBones(true);
      return;
    }
    this.mode = mode;
    this.applyMode(mode);
  }

  getMode(): BoneBodyMode {
    return this.mode;
  }

  /**
   * Copy bone world transforms → rigid bodies.
   * `immediate`: setTranslation (mode switches); else kinematic next pose.
   * Under global dynamic mode, dynamic-slot humans are skipped unless immediate
   * (they free-fall); other humans stay kinematic and track animation.
   */
  syncFromBones(immediate = false): void {
    if (!this.physics || this.entries.length === 0) return;

    for (const e of this.ordered) {
      e.bone.updateWorldMatrix(true, false);
    }

    for (const e of this.entries) {
      const isDynamicSlot =
        this.mode === "dynamic" && this.dynamicHumanIds.has(e.humanId);
      if (isDynamicSlot && !immediate) continue;

      e.bone.matrixWorld.decompose(_pos, _quat, _scale);
      if (immediate || isDynamicSlot) {
        e.body.setTranslation({ x: _pos.x, y: _pos.y, z: _pos.z }, true);
        e.body.setRotation(
          { x: _quat.x, y: _quat.y, z: _quat.z, w: _quat.w },
          true,
        );
        e.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        e.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      } else {
        e.body.setNextKinematicTranslation({ x: _pos.x, y: _pos.y, z: _pos.z });
        e.body.setNextKinematicRotation({
          x: _quat.x,
          y: _quat.y,
          z: _quat.z,
          w: _quat.w,
        });
      }
    }
  }

  /** Write rigid body transforms → bones (ragdoll). Parent-first; dynamic slots only. */
  applyToBones(): void {
    if (this.mode !== "dynamic" || this.entries.length === 0) return;

    for (const e of this.ordered) {
      if (!this.dynamicHumanIds.has(e.humanId)) continue;

      const t = e.body.translation();
      const r = e.body.rotation();
      _pos.set(t.x, t.y, t.z);
      _quat.set(r.x, r.y, r.z, r.w);

      const parent = e.bone.parent;
      if (parent) {
        parent.updateWorldMatrix(true, false);
        _parentInv.copy(parent.matrixWorld).invert();
        _mat.compose(_pos, _quat, _scale.set(1, 1, 1));
        _mat.premultiply(_parentInv);
        _mat.decompose(_localPos, _localQuat, _localScale);
        e.bone.position.copy(_localPos);
        e.bone.quaternion.copy(_localQuat);
      } else {
        e.bone.position.copy(_pos);
        e.bone.quaternion.copy(_quat);
      }
      e.bone.updateMatrix();
      e.bone.updateWorldMatrix(false, false);
    }
  }

  clear(): void {
    const physics = this.physics;
    if (physics) {
      for (const e of this.entries) {
        if (e.joint) {
          physics.world.removeImpulseJoint(e.joint, true);
        }
        physics.world.removeCollider(e.collider, true);
        physics.world.removeRigidBody(e.body);
      }
    }
    this.entries = [];
    this.ordered = [];
    this.dynamicHumanIds.clear();
  }

  dispose(): void {
    this.disposed = true;
    this.clear();
    this.physics = null;
  }

  private applyMode(mode: BoneBodyMode): void {
    if (!this.physics || this.entries.length === 0) return;
    const R = this.physics.R;

    this.syncFromBones(true);

    for (const e of this.entries) {
      const allowDynamic = this.dynamicHumanIds.has(e.humanId);
      if (mode === "dynamic" && allowDynamic) {
        e.body.setBodyType(R.RigidBodyType.Dynamic, true);
        e.body.wakeUp();
      } else if (mode === "fixed") {
        e.body.setBodyType(R.RigidBodyType.Fixed, true);
      } else {
        // Non-budget humans stay kinematic even in global ragdoll (tier low).
        e.body.setBodyType(R.RigidBodyType.KinematicPositionBased, true);
      }
    }
  }

  private buildHuman(
    physics: PhysicsWorld,
    human: LoadedHuman,
    humanIndex: number,
    humanCount: number,
  ): void {
    const bones = collectBones(human);
    if (bones.length === 0) return;

    const R = physics.R;
    const byBone = new Map<THREE.Bone, BoneBodyEntry>();
    const groups = packGroups(
      humanMembership(humanIndex),
      humanFilter(humanIndex, humanCount),
    );

    for (const bone of bones) {
      const segment = primaryChildSegment(bone);
      const length = segment?.length ?? 0;
      const boneChildren = bone.children.filter((c) => (c as THREE.Bone).isBone);

      if (length < MIN_BONE_LEN && boneChildren.length === 0) {
        const isRoot = !bone.parent || !(bone.parent as THREE.Bone).isBone;
        if (!isRoot) continue;
      }

      const radius = segment
        ? THREE.MathUtils.clamp(segment.length * 0.12, 0.025, 0.1)
        : 0.06;
      const halfHeight =
        segment && segment.length >= MIN_BONE_LEN
          ? Math.max(0.001, segment.length * 0.5 - radius)
          : 0.001;

      bone.updateWorldMatrix(true, false);
      bone.matrixWorld.decompose(_pos, _quat, _scale);

      const mass = Math.max(0.05, (halfHeight * 2 + radius * 2) * radius * radius * 40);
      const bodyDesc = R.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(_pos.x, _pos.y, _pos.z)
        .setRotation({ x: _quat.x, y: _quat.y, z: _quat.z, w: _quat.w })
        .setCanSleep(true)
        .setLinearDamping(0.35)
        .setAngularDamping(0.85);
      const body = physics.world.createRigidBody(bodyDesc);

      let collDesc;
      if (segment && segment.length >= MIN_BONE_LEN) {
        // Capsule along bone→child in body/bone local space.
        _dir.copy(segment.dirLocal);
        const dirLen = _dir.length();
        if (dirLen > 1e-8) {
          _dir.multiplyScalar(1 / dirLen);
          _collQuat.setFromUnitVectors(_yAxis, _dir);
        } else {
          _collQuat.identity();
        }
        const mid = segment.length * 0.5;
        collDesc = R.ColliderDesc.capsule(halfHeight, radius)
          .setTranslation(_dir.x * mid, _dir.y * mid, _dir.z * mid)
          .setRotation({
            x: _collQuat.x,
            y: _collQuat.y,
            z: _collQuat.z,
            w: _collQuat.w,
          })
          .setMass(mass)
          .setFriction(0.75)
          .setRestitution(0.05)
          .setCollisionGroups(groups)
          .setSolverGroups(groups);
      } else {
        collDesc = R.ColliderDesc.ball(radius)
          .setMass(mass)
          .setFriction(0.75)
          .setRestitution(0.05)
          .setCollisionGroups(groups)
          .setSolverGroups(groups);
      }
      const collider = physics.world.createCollider(collDesc, body);

      const entry: BoneBodyEntry = {
        bone,
        humanId: human.def.id,
        body,
        collider,
        joint: null,
        parentEntry: null,
        halfHeight,
        radius,
      };
      this.entries.push(entry);
      byBone.set(bone, entry);
    }

    // Spherical joints: anchors in body-local space (body == bone world).
    for (const entry of byBone.values()) {
      const parentBone = entry.bone.parent as THREE.Bone | null;
      if (!parentBone || !(parentBone as THREE.Bone).isBone) continue;
      const parentEntry = byBone.get(parentBone);
      if (!parentEntry) continue;

      entry.parentEntry = parentEntry;

      // Child origin in parent bone/body local frame.
      entry.bone.getWorldPosition(_childPos);
      parentBone.updateWorldMatrix(true, false);
      _parentInv.copy(parentBone.matrixWorld).invert();
      _localPos.copy(_childPos).applyMatrix4(_parentInv);

      const jointData = R.JointData.spherical(
        { x: _localPos.x, y: _localPos.y, z: _localPos.z },
        { x: 0, y: 0, z: 0 },
      );
      entry.joint = physics.world.createImpulseJoint(
        jointData,
        parentEntry.body,
        entry.body,
        true,
      );
    }
  }
}

function collectBones(human: LoadedHuman): THREE.Bone[] {
  const bones = new Set<THREE.Bone>();
  human.root.traverse((obj) => {
    const skinned = obj as THREE.SkinnedMesh;
    if (skinned.isSkinnedMesh && skinned.skeleton) {
      for (const b of skinned.skeleton.bones) bones.add(b);
    }
    if ((obj as THREE.Bone).isBone) bones.add(obj as THREE.Bone);
  });
  return [...bones];
}

function primaryChildSegment(bone: THREE.Bone): {
  length: number;
  dir: THREE.Vector3;
  dirLocal: THREE.Vector3;
  child: THREE.Bone;
} | null {
  const children = bone.children.filter((c) => (c as THREE.Bone).isBone) as THREE.Bone[];
  if (children.length === 0) return null;

  bone.updateWorldMatrix(true, false);
  bone.getWorldPosition(_pos);

  let best: THREE.Bone | null = null;
  let bestLen = 0;
  let bestDir = new THREE.Vector3();
  let bestLocal = new THREE.Vector3();

  for (const child of children) {
    child.updateWorldMatrix(true, false);
    child.getWorldPosition(_childPos);
    _dir.subVectors(_childPos, _pos);
    const len = _dir.length();
    if (len > bestLen) {
      bestLen = len;
      best = child;
      bestDir.copy(_dir);
      // Prefer direct-child local position when available.
      bestLocal.copy(child.position);
      if (bestLocal.lengthSq() < 1e-10) {
        _parentInv.copy(bone.matrixWorld).invert();
        bestLocal.copy(_childPos).applyMatrix4(_parentInv);
      }
    }
  }
  if (!best || bestLen < 1e-6) return null;
  return { length: bestLen, dir: bestDir, dirLocal: bestLocal, child: best };
}

function topologicalOrder(entries: BoneBodyEntry[]): BoneBodyEntry[] {
  const set = new Set(entries);
  const result: BoneBodyEntry[] = [];
  const visiting = new Set<BoneBodyEntry>();

  function visit(e: BoneBodyEntry): void {
    if (!set.has(e) || visiting.has(e)) return;
    visiting.add(e);
    if (e.parentEntry) visit(e.parentEntry);
    if (!result.includes(e)) result.push(e);
  }

  for (const e of entries) visit(e);
  return result;
}
